// CONTRACT:C1-MAP-CLIENT.1.0
// COP — THE THROUGHLINE (Item 3). docs/ux/SHELL-COMPOSITION.md §2.6.
//
// The ONE persistent front door. At rest it is a calm thin omnibox RUNG welded to
// the bottom edge (no transcript, no AI logo). On "/" or ⌘K it expands into the
// modeless omnibox: ONE parser → one TypedQuery AST → one ranked result list across
// FIVE rungs the operator never declares —
//   GO     locate → fly + halo            (handle.locate)
//   FILTER re-scope in place, fade-not-hide (handle.applyFilter)
//   OPEN   summon a panel                  (handle.runQuery / DO run)
//   DO     stage an action card            (handle.stageAction + folded `commands`)
//   ASK    NL transpiles DOWN into FILTER chips / DO cards, shown BEFORE commit
//          (the Glass Compiler) — a preview, never an auto-run.
//
// REUSE (not a fork): this is the CommandPalette scrim / dialog / focus-trap / ⌘K
// listener / fuzzy-match generalised. The `Command` interface folds in as DO rung
// entries (passed by the orchestrator). The summoned dropdown reuses the cmdk modal
// body; the resting rung is a NEW thin docked element.
//
// LAWS: the dropdown is a HEAVY summon → claims the R-LAW-3 slot via heavyPanel,
// so opening it collapses any other heavy surface (Catalog flip, Ledger graph,
// SettingsDrawer). Esc pops ONE level via useEscapeStack (results → filter chips →
// close). Self-teaching: each result row carries its keyboard shortcut as a label.
//
// GLOMAR: every list this renders comes from host-supplied, visible-only data
// (status.queryResults). It NEVER renders a padlock, greyed row, counted gap or
// "Access Denied" — a withheld object is simply absent upstream.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type {
  QueryResult,
  QueryRung,
  ActiveFilter,
} from "../host/shellContract.ts";
// Composite seam types are type-only (zero runtime / zero MapLibre pulled in),
// matching the Hud precedent. The orchestrator passes the live values as props.
import type { CopStatus, CopHandle } from "../host/cop.ts";
import { useHeavyPanel } from "./heavyPanel.ts";
import { useEscapeStack } from "./useEscapeStack.ts";
import "./throughline.css";

/** A single folded-in DO command (the existing ⌘K palette commands). Structurally
 *  the CommandPalette `Command` — kept local so this file imports no component. */
export interface DoCommand {
  id: string;
  title: string;
  hint?: string;
  /** Palette taxonomy group (ACTION/PLUGIN/AI/SCOPE/ACCESS/VIEW/AUDIT/IDENT). Display-only:
   *  rendered as the per-row teal mono group column (mock paletteItems, line 1070). Optional +
   *  backward-compatible — a command without a group renders with a blank column. */
  group?: string;
  run: () => void;
}

export interface ThroughlineProps {
  /** The live host snapshot — read directly (always-present per GROUP 0). */
  status: CopStatus;
  /** The imperative host seam. */
  handle: CopHandle | null;
  /** Existing palette commands, folded in as DO-rung entries by the orchestrator. */
  commands?: DoCommand[];
  /** Whether the COP Copilot dock is open — drives the persistent rung toggle's active state. */
  copilotOpen?: boolean;
  /** Toggle the COP Copilot dock (the persistent teal "Copilot" button in the resting rung). */
  onToggleCopilot?: () => void;
  /** Run a one-shot prompt (the resting rung's "Top threats" chip / placeholder click) — opens
   *  the copilot and asks it the query. The host owns the copilot ask seam. */
  onPrompt?: (query: string) => void;
  /** C3 — the FREE-TEXT ASK sink (RATIFIED): a sigil-less natural-language sentence (intent ===
   *  'ask') on ↵ routes HERE instead of the host runQuery transpile. App.tsx wires it to open the
   *  bottom-center copilot thread + handle.copilotApi.ask(text). EXPLICIT sigil'd queries
   *  (>/@/#/go:/filter:/open:/do:) still transpile via the Glass-Compiler — only the bare ASK rung
   *  changes. The copilot reasons ONLY over what the persona is cleared to see (Glomar-safe). */
  onAsk?: (text: string) => void;
}

/* ---------------------------------------------------------------------------
 * ONE PARSER → ONE TypedQuery AST
 * The operator never declares a rung. A leading sigil (or a natural sentence)
 * decides intent; everything else is a free-text term ranked across rungs.
 * ------------------------------------------------------------------------- */

/** The single typed-query AST every keystroke compiles to. */
export interface TypedQuery {
  /** The raw text the operator typed (verbatim, for the host to re-parse). */
  raw: string;
  /** The bare term with any leading intent sigil stripped. */
  term: string;
  /** Intent inferred from sigil/shape; "auto" = rank across all rungs. */
  intent: QueryRung | "auto";
}

/** Intent sigils the operator MAY type but never has to. Self-teaching: the
 *  resting rung hint surfaces them. */
const SIGILS: Record<string, QueryRung> = {
  ">": "do", // ">recenter" — an action, cmdk-familiar
  "@": "go", // "@track-7" — locate an entity
  "#": "filter", // "#hostile" — re-scope in place
  "?": "ask", // "?who is near the FLOT" — natural language
};

/** Parse one input string into the TypedQuery AST. Pure; no host contact. */
export function parseQuery(raw: string): TypedQuery {
  const trimmed = raw.trimStart();
  const head = trimmed.charAt(0);
  if (head in SIGILS) {
    return { raw, term: trimmed.slice(1).trim(), intent: SIGILS[head] };
  }
  // A multi-word natural sentence with no sigil reads as ASK (the Glass Compiler
  // will transpile it DOWN into FILTER chips / DO cards before any commit).
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 3) return { raw, term: trimmed, intent: "ask" };
  return { raw, term: trimmed, intent: "auto" };
}

/* ---------------------------------------------------------------------------
 * Fuzzy match (carried verbatim from CommandPalette) — subsequence scoring.
 * ------------------------------------------------------------------------- */
function fuzzyScore(query: string, text: string): number | null {
  if (query === "") return 0;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  let ti = 0;
  let score = 0;
  let lastHit = -1;
  for (let qi = 0; qi < q.length; qi++) {
    const ch = q[qi];
    const found = t.indexOf(ch, ti);
    if (found === -1) return null;
    if (lastHit !== -1) score += found - lastHit - 1;
    if (qi === 0) score += found;
    lastHit = found;
    ti = found + 1;
  }
  return score;
}

/** Rung display order + label (GO/FILTER/OPEN/DO/ASK). The operator reads, never
 *  declares, these — they are the headers of the single ranked list. */
const RUNG_ORDER: QueryRung[] = ["go", "filter", "open", "do", "ask"];
const RUNG_LABEL: Record<QueryRung, string> = {
  go: "GO",
  filter: "FILTER",
  open: "OPEN",
  do: "DO",
  ask: "ASK",
};

/** A flat, ranked, rung-tagged row — the union of host queryResults and folded DO
 *  commands. `run` is the local executor when the host hasn't supplied a verb. */
interface Row {
  id: string;
  rung: QueryRung;
  title: string;
  shortcut?: string;
  /** Palette taxonomy group (DO rows only) — the per-row teal mono group column. */
  group?: string;
  /** Local executor for folded DO commands; host rungs run via the seam. */
  localRun?: () => void;
}

export function Throughline({
  status,
  handle,
  commands,
  copilotOpen,
  onToggleCopilot,
  onPrompt,
  onAsk,
}: ThroughlineProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const listboxId = useId();

  // The dropdown is a HEAVY summon (R-LAW-3): claiming it collapses any other heavy
  // surface, and being DISPLACED by one collapses us.
  const heavy = useHeavyPanel("throughline");

  const setOpenAndClaim = useCallback(
    (next: boolean) => {
      setOpen(next);
      heavy.setActive(next);
    },
    [heavy],
  );

  // Global "/" or ⌘K / Ctrl-K summon. "/" is ignored while typing in a field so it
  // never steals a real slash. (Carried from the cmdk listener, generalised.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const metaK =
        (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      const slash =
        e.key === "/" && !isEditableTarget(e.target) && !e.metaKey && !e.ctrlKey && !e.altKey;
      if (metaK) {
        e.preventDefault();
        setOpenAndClaim(!open);
        return;
      }
      if (slash && !open) {
        e.preventDefault();
        setOpenAndClaim(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpenAndClaim]);

  // R-LAW-3: if another heavy panel claims the slot while we're open, collapse.
  useEffect(() => {
    if (open && !heavy.isActive) setOpen(false);
  }, [open, heavy.isActive]);

  // Esc pops ONE level: clear an active FILTER's chips view first, else close.
  useEscapeStack(() => setOpenAndClaim(false), open);

  // On open: remember focus, reset, focus the input. On close: restore focus.
  useEffect(() => {
    if (open) {
      restoreFocusRef.current = document.activeElement as HTMLElement | null;
      setQuery("");
      setActiveIndex(0);
      const id = window.requestAnimationFrame(() => inputRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
    restoreFocusRef.current?.focus?.();
    return undefined;
  }, [open]);

  const ast = useMemo(() => parseQuery(query), [query]);

  // Fan the AST to the host so it can populate status.queryResults. Cheap stub-safe.
  useEffect(() => {
    if (!open) return;
    handle?.runQuery(ast);
  }, [open, ast, handle]);

  // The DO universe folded from the existing palette commands.
  const doRows = useMemo<Row[]>(
    () =>
      (commands ?? []).map((c) => ({
        id: `do:${c.id}`,
        rung: "do" as QueryRung,
        title: c.title,
        shortcut: c.hint,
        ...(c.group ? { group: c.group } : {}),
        localRun: c.run,
      })),
    [commands],
  );

  // The host's ranked results (GO/FILTER/OPEN/ASK + any host DO).
  const hostRows = useMemo<Row[]>(
    () =>
      status.queryResults.map((r: QueryResult) => ({
        id: `host:${r.id}`,
        rung: r.rung,
        title: r.title,
        shortcut: r.shortcut,
      })),
    [status.queryResults],
  );

  // ONE ranked list across the five rungs. The query fuzzy-filters; the parser's
  // intent (if any sigil) restricts to that rung. Grouped by RUNG_ORDER for the
  // self-teaching headers, but a single flat index drives keyboard nav.
  const rows = useMemo<Row[]>(() => {
    const all = [...hostRows, ...doRows];
    const term = ast.term.trim();
    const intentFiltered =
      ast.intent === "auto" || ast.intent === "ask"
        ? all
        : all.filter((r) => r.rung === ast.intent);

    const scored = intentFiltered
      .map((r) => {
        const titleScore = fuzzyScore(term, r.title);
        const hintScore = r.shortcut ? fuzzyScore(term, r.shortcut) : null;
        const best =
          titleScore === null
            ? hintScore
            : hintScore === null
              ? titleScore
              : Math.min(titleScore, hintScore + 2);
        return best === null ? null : { row: r, score: best };
      })
      .filter((x): x is { row: Row; score: number } => x !== null);

    // Stable order: by rung band first (so the GO/FILTER/OPEN/DO/ASK headers read
    // top-to-bottom), then by fuzzy score within the band.
    scored.sort((a, b) => {
      const ra = RUNG_ORDER.indexOf(a.row.rung);
      const rb = RUNG_ORDER.indexOf(b.row.rung);
      if (ra !== rb) return ra - rb;
      return a.score - b.score;
    });
    return scored.map((x) => x.row);
  }, [hostRows, doRows, ast]);

  // The active in-place FILTER (fade-not-hide), shown as committed chips at the top
  // of the dropdown so the operator always sees the current scope.
  const filter: ActiveFilter | null = status.activeFilter;

  // ISOLATE-A-CLASS bands: the classification levels the operator may FADE. Sourced from
  // queryScope.available (the levels the persona is cleared FOR — NOT a content enumeration,
  // so no Glomar leak: it reveals nothing about what objects exist). High→low so the most-
  // sensitive band reads first. activeFadeBand reflects the live "Fade class: <BAND>" filter so
  // the chip shows pressed + toggles off. Memoised off the two scalar inputs.
  const fadeBands: string[] = useMemo(
    () => [...(status.queryScope.available ?? [])].reverse(),
    [status.queryScope.available],
  );
  const activeFadeBand: string | null = useMemo(() => {
    const label = status.activeFilter?.label ?? "";
    const m = /^Fade class:\s*(.+)$/.exec(label);
    return m ? m[1].trim() : null;
  }, [status.activeFilter]);

  // ASK preview: when the AST is an ASK and the host has transpiled it into rows,
  // we show them under an explicit "preview — press ↵ to commit" affordance. The
  // Glass Compiler shows chips/cards BEFORE commit; it never auto-runs.
  const isAskPreview = ast.intent === "ask" && ast.term.length > 0;

  // Clamp the active index when results shrink.
  useEffect(() => {
    setActiveIndex((i) => (rows.length === 0 ? 0 : Math.min(i, rows.length - 1)));
  }, [rows.length]);

  // Keep the active option scrolled into view.
  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelectorAll("[role=option]")[
      activeIndex
    ] as HTMLElement | undefined;
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  /** C3 — route a bare natural-language ASK to the COP Copilot (RATIFIED): close the omnibox,
   *  then hand the whole sentence to the onAsk sink (App.tsx opens the bottom-center thread +
   *  handle.copilotApi.ask). Returns true when it consumed the ↵ (so the row `choose` is skipped).
   *  Falls back to the host transpile (choose's ask case) when no onAsk sink is wired. */
  const askCopilot = useCallback((): boolean => {
    if (ast.intent !== "ask") return false;
    const text = ast.term.trim();
    if (!onAsk || text.length === 0) return false;
    setOpenAndClaim(false);
    onAsk(text);
    return true;
  }, [ast, onAsk, setOpenAndClaim]);

  /** Run a row through the correct rung verb on the host seam. The dropdown closes
   *  first (so a fly/halo lands on the bare map), then the verb fires. */
  const choose = useCallback(
    (row: Row | undefined) => {
      if (!row) return;
      setOpenAndClaim(false);
      switch (row.rung) {
        case "go":
          handle?.locate(stripPrefix(row.id));
          break;
        case "filter":
          handle?.applyFilter({ raw: ast.raw, term: ast.term, rowId: row.id });
          break;
        case "open":
          handle?.runQuery(ast);
          break;
        case "do":
          if (row.localRun) row.localRun();
          else handle?.stageAction({ id: row.id, title: row.title });
          break;
        case "ask":
          // C3: ASK routes to the COP Copilot when an onAsk sink is wired (the RATIFIED design —
          // free-text NL asks the copilot, which reasons only over the cleared-visible set). Falls
          // back to the host transpile (the Glass-Compiler preview) when no sink is present.
          if (onAsk && ast.term.trim().length > 0) onAsk(ast.term.trim());
          else handle?.runQuery(ast);
          break;
        default:
          break;
      }
    },
    [handle, ast, setOpenAndClaim, onAsk],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => (rows.length === 0 ? 0 : (i + 1) % rows.length));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) =>
          rows.length === 0 ? 0 : (i - 1 + rows.length) % rows.length,
        );
        break;
      case "Enter":
        e.preventDefault();
        // C3: a bare ASK sentence routes to the COP Copilot, not the row `choose` transpile.
        // EXPLICIT sigil'd queries (non-ask intents) fall through to the ranked-row verb.
        if (askCopilot()) break;
        choose(rows[activeIndex]);
        break;
      case "Tab":
        // Minimal focus trap: only the input is tabbable, keep focus on it.
        e.preventDefault();
        inputRef.current?.focus();
        break;
      // Escape is handled by useEscapeStack (one-level pop).
      default:
        break;
    }
  };

  // --- the resting RUNG: the mock's bottom bar (line 498) — a persistent teal Copilot
  // toggle + a rounded omnibox PILL (search glyph + Public-Sans placeholder + a "Top
  // threats" prompt chip + an inline ⌘K button). The pill summons the dropdown; the
  // toggle/chip drive the copilot. -----------------------------------------------------
  return (
    <div className="throughline">
      <div className="throughline__rung">
        {onToggleCopilot ? (
          <button
            type="button"
            className="throughline__copilot-btn"
            data-active={copilotOpen ? "true" : "false"}
            aria-pressed={copilotOpen ? "true" : "false"}
            aria-label={copilotOpen ? "Hide COP Copilot" : "Open COP Copilot"}
            onClick={onToggleCopilot}
          >
            <span className="throughline__copilot-dot" aria-hidden="true" />
            <span className="throughline__copilot-label">Copilot</span>
          </button>
        ) : null}

        <div
          className="throughline__pill"
          role="button"
          tabIndex={0}
          aria-label="Open the omnibox"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpenAndClaim(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpenAndClaim(true);
            }
          }}
        >
          <span className="throughline__glyph" aria-hidden="true">
            ⌕
          </span>
          <span className="throughline__placeholder">
            {filter ? (
              <span className="throughline__rung-filter">
                {filter.label}
                {filter.chips.length > 0 ? ` · ${filter.chips.join(" · ")}` : ""}
              </span>
            ) : (
              'Ask the picture or search — “show me the top threats”…'
            )}
          </span>
          {onPrompt ? (
            <button
              type="button"
              className="throughline__promptchip"
              onClick={(e) => {
                // The chip runs the copilot directly; it must NOT also summon the omnibox.
                e.stopPropagation();
                onPrompt("Top threats");
              }}
            >
              Top threats
            </button>
          ) : null}
          <button
            type="button"
            className="throughline__kbtn"
            aria-label="Open command palette"
            onClick={(e) => {
              e.stopPropagation();
              setOpenAndClaim(true);
            }}
          >
            ⌘K
          </button>
        </div>
      </div>

      {open ? (
        <div
          className="throughline__scrim"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpenAndClaim(false);
          }}
        >
          <div
            className="throughline__panel"
            role="dialog"
            aria-modal="true"
            aria-label="Omnibox"
            onKeyDown={onKeyDown}
          >
            <div className="throughline__field">
              <span className="throughline__field-prompt" aria-hidden="true">
                {ast.intent === "ask" ? "?" : ">"}
              </span>
              <input
                ref={inputRef}
                className="throughline__input"
                type="text"
                placeholder="Ask the picture or search…"
                value={query}
                spellCheck={false}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                role="combobox"
                aria-expanded="true"
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={
                  rows.length > 0 ? `${listboxId}-opt-${activeIndex}` : undefined
                }
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
              />
              {ast.intent !== "auto" ? (
                <span className="throughline__intent" aria-hidden="true">
                  {RUNG_LABEL[ast.intent]}
                </span>
              ) : null}
            </div>

            {filter ? (
              <div className="throughline__filterbar" aria-label="Active filter">
                <span className="throughline__filterbar-label">SCOPE</span>
                <span className="throughline__chip throughline__chip--active">
                  {filter.label}
                </span>
                {filter.chips.map((c) => (
                  <span key={c} className="throughline__chip">
                    {c}
                  </span>
                ))}
                <button
                  type="button"
                  className="throughline__chip throughline__chip--clear"
                  style={{ background: "none", cursor: "pointer" }}
                  aria-label="Clear filter"
                  title="Clear the active filter"
                  onClick={() => handle?.applyFilter({ term: "" })}
                >
                  ✕ clear
                </button>
              </div>
            ) : null}

            {/* ISOLATE-A-CLASS (B1, fade-not-hide): pick a classification BAND to FADE — matching
                visible entities dim out (never removed; denial stays Glomar). The bands are the
                operator's AVAILABLE working levels (queryScope.available — NOT a content leak: it
                is what the persona is cleared FOR, not what exists). Clicking an active band toggles
                the fade off. Routes through handle.applyFilter({classification}), the same fade-not-
                hide seam the term filter uses. */}
            {fadeBands.length > 0 ? (
              <div className="throughline__filterbar throughline__fadeband" aria-label="Fade a classification band">
                <span className="throughline__filterbar-label">FADE CLASS</span>
                {fadeBands.map((band) => {
                  const isActive = activeFadeBand === band;
                  return (
                    <button
                      key={band}
                      type="button"
                      className={`throughline__chip throughline__chip--band${isActive ? " throughline__chip--active" : ""}`}
                      style={{ background: "none", cursor: "pointer" }}
                      aria-pressed={isActive}
                      title={isActive ? `Stop fading ${band}` : `Fade ${band} entities (kept in the picture, dimmed)`}
                      onClick={() =>
                        // Toggle: clicking the active band clears the fade; else fade that band.
                        handle?.applyFilter(isActive ? { term: "" } : { classification: band })
                      }
                    >
                      {band.replace(/_/g, " ")}
                    </button>
                  );
                })}
              </div>
            ) : null}

            {isAskPreview ? (
              <div className="throughline__askhint" role="note">
                {onAsk ? (
                  <>
                    press <kbd>↵</kbd> to ask the COP Copilot — reasons only over
                    what you’re cleared to see
                  </>
                ) : (
                  <>
                    ASK preview — transpiles to filters / actions; press{" "}
                    <kbd>↵</kbd> to stage before commit
                  </>
                )}
              </div>
            ) : null}

            {rows.length === 0 ? (
              <div className="throughline__empty">
                {query.trim() === ""
                  ? "Type to go, filter, open, do, or ask"
                  : "No matches"}
              </div>
            ) : (
              <ul
                className="throughline__list"
                id={listboxId}
                role="listbox"
                ref={listRef}
              >
                {renderGrouped(rows, activeIndex, listboxId, setActiveIndex, choose)}
              </ul>
            )}

            <div className="throughline__footer" aria-hidden="true">
              <span>
                <kbd>↑</kbd>
                <kbd>↓</kbd> navigate
              </span>
              <span>
                <kbd>↵</kbd> run
              </span>
              <span>
                <kbd>esc</kbd> close
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Render the flat ranked list with self-teaching rung headers. A single running
 *  index keeps keyboard nav aligned to the visible order. */
function renderGrouped(
  rows: Row[],
  activeIndex: number,
  listboxId: string,
  setActiveIndex: (i: number) => void,
  choose: (r: Row) => void,
): JSX.Element[] {
  const out: JSX.Element[] = [];
  let lastRung: QueryRung | null = null;
  rows.forEach((row, i) => {
    if (row.rung !== lastRung) {
      lastRung = row.rung;
      out.push(
        <li
          key={`hdr-${row.rung}`}
          className="throughline__group"
          role="presentation"
          aria-hidden="true"
        >
          {RUNG_LABEL[row.rung]}
        </li>,
      );
    }
    out.push(
      <li
        key={row.id}
        id={`${listboxId}-opt-${i}`}
        className="throughline__item"
        role="option"
        aria-selected={i === activeIndex}
        onMouseMove={() => setActiveIndex(i)}
        onMouseDown={(e) => {
          e.preventDefault(); // keep focus in the input
          choose(row);
        }}
      >
        {/* The mock's per-row teal mono group column (paletteItems, line 1070). Fixed-width
            so titles align; blank when a row carries no taxonomy group. */}
        <span className="throughline__item-group" aria-hidden="true">
          {row.group ?? ""}
        </span>
        <span className="throughline__item-title">{row.title}</span>
        {row.shortcut ? (
          // Self-teaching: the row carries its keyboard shortcut as its label.
          <span className="throughline__item-shortcut">{row.shortcut}</span>
        ) : null}
      </li>,
    );
  });
  return out;
}

/** True when a keystroke originated inside an editable control — so a bare "/"
 *  there types a literal slash instead of summoning. */
function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    el.isContentEditable === true
  );
}

/** Strip the local `host:` / `do:` id prefix back to the host envelope id. */
function stripPrefix(id: string): string {
  const i = id.indexOf(":");
  return i === -1 ? id : id.slice(i + 1);
}
